import { Input,  Component, OnInit, forwardRef, OnDestroy, ChangeDetectorRef, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl } from '@angular/forms';

import { BehaviorSubject, Subscription, filter, map, tap } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AbstractFormFieldComponent } from '../../FormField/abstract-form-field.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import {
    ManageContentLibraryDialogComponent
} from '../../../../ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ContentLibraryFieldModel } from '../../../../../Common/PageBuilder/models/content-library-field-model';
import { MediaSummaryModel } from '../../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import { FileUploadModel } from '../../../../../Common/Forms/models/file-upload.model';

import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { TextFieldModel } from '../../../../../Common/PageBuilder/models/text-field.model';
import { ResizeDirectionEnum } from '../../../../../Common/Forms/components/TextareaInput/textarea-input.component';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';

import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { ContentLibraryMediaService } from '../../../../../Common/ContentLibrary/services/content-library-media.service';
import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaServiceErrorCodesEnum } from '../../../../../api/mediaService/mediaResults.api.model';

@Component({
    selector: 'agpr-content-library-panel',
    templateUrl: './content-library-panel.component.html',
    styleUrls: ['./content-library-panel.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ContentLibraryPanelFieldComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ContentLibraryPanelFieldComponent extends AbstractFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    @Input() config: ContentLibraryFieldModel;
    @Input() declare field: TextFieldModel;
    @Input() socialTheme: boolean = false;
    @Input() buttonTooltip: string = 'Social.intelligence.themeManagement.themeCard.addThemeImage';

    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    contentLibraryImages: MediaSummaryModel[] = [];
    value: FileUploadModel;
    subscriptions: Subscription[] = [];
    acceptedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    uploadIcon: string;

    uploadOptions = [
        {
            label: 'Mention.editDialog.image.label',
            command: () => {
                this.value = new FileUploadModel();
                this.value.url = '';
                this.emitChanges();
            }
        },
        {
            label: 'Mention.editDialog.image.browser',
            description: 'Mention.editDialog.image.browser.description',
            command: () => {
                this.onImageUpload();
            }
        },
        {
            label: 'Mention.editDialog.image.contentLibrary',
            command: () => {
                this.openContentLibrary();
            }
        }
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        public readonly contentLibraryMediaService: ContentLibraryMediaService,
        private mediaServiceApi: MediaServiceApi,
        private cd: ChangeDetectorRef
    ) {
        super();
    }

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    ngOnInit(): void {
        if (!this.value) {
            this.value = new FileUploadModel();
            this.value.url = null;
        }

        if (this.config?.defaultValue) {
            this.value.url = this.config.defaultValue;
        }

        this.subscriptions.push(
            this.contentLibraryMediaService.all$.subscribe(media => {
                this.contentLibraryImages = media;
                if (this.value.url || this.value.url === '') {
                    const matchedFile = media.find(item => item.url === this.value.url);
                    if (matchedFile) {
                        this.value.id = matchedFile.id;
                        this.value.name = matchedFile.name;
                        this.value.size = matchedFile.size;
                        this.value.mimeType = matchedFile.mimeType;
                        this.value.url = matchedFile.url;
                    }
                    if (this.value.altText) {
                        this.value.showAltText = true;
                    }
                    this.value.touched = false;
                    this.updateUploadIcon();
                }
            })
        );
    }


    ngOnChanges() {
        if (this.value?.url || this.value?.url === '') {
            this.cd.detectChanges();
        }
        this.updateUploadIcon();
    }

    onImageUpload(): void {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (file) {
                this.uploadFile(file);
            }
            fileInput.value = '';
        };
        fileInput.click();
    }

    writeValue(value: FileUploadModel): void {
        this.value = value;
    }

    openContentLibrary(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                selectionMode: true,
                singleSelect: true,
                acceptedFileTypes: this.acceptedFileTypes,
                navBarLaunch: false
            },
            outputs: {

                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }

                    const invalidFiles = selectedFiles.filter(file =>
                        !this.acceptedFileTypes.includes(file.mimeType)
                    );

                    if (invalidFiles.length > 0) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                            detail: this.localizationService.transform('PageBuilder.error.fileTypeMessage', {
                                types: io.inputs.acceptedFileTypes.map(type => type.replace('image/', '')).join(', ')
                            })
                        });
                        return;
                    }
                    if (selectedFiles?.length) {
                        const selectedFile = selectedFiles[0];

                        this.assignMediaToValue(selectedFile);
                        this.value.mimeType = selectedFile.mimeType;
                        this.emitChanges();
                        this.updateUploadIcon();
                    }
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    onUrlInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.value.url = input.value;
        if (!this.value.touched) {
            this.value.touched = true;
        }
    }

    onUrlBlur(): void {
        this.value.touched = true;
        const trimmedUrl = this.value.url?.trim();
        this.value.url = trimmedUrl;

        if (!trimmedUrl) {
            this.value.url = '';
            this.value.altText = '';
            this.value.hasError = true;
            this.value.errorMessage = 'Briefings.error.invalidImageUrl';

            this.emitChanges();
        } else {
            this.urlBlur();
            this.emitChanges();
        }
    }

    urlBlur(): boolean {
        // skips validation if the user hasn’t interacted with the input yet.
        if (!this.value.touched) {
            return true;
        }

        this.value.hasError = false;
        this.value.errorMessage = null;

        if (!this.value.url) {
            return true;
        }

        try {
            new URL(this.value.url);
            return true;
        } catch {
            this.value.errorMessage = 'Briefings.error.invalidImageUrl';
            this.value.hasError = true;
            return false;
        }
    }

    emitChanges(): void {
        this.updateUploadIcon();
        if (this.socialTheme) {
            this.changes.emit(this.value.url);
        } else {
            this.changes.emit(this.value);
        }
        this.onModelChange(this.value);
    }

    changeAltText(file: FileUploadModel): void {
        if (!file.showAltText) {
            this.value.altText = '';
        }
        this.emitChanges();
    }

    updateUploadIcon(): void {
        if (this.value && (this.value.url || this.value.url === '')) {
            this.uploadIcon = 'fa-regular fa-duotone fa-pen';
        } else {
            this.uploadIcon = 'fa-regular fa-duotone fa-square-plus';
        }
    }

    private assignMediaToValue(media: any): void {
        this.value.id = media.id;
        this.value.name = media.name;
        this.value.size = media.size;
        this.value.url = media.url;
        this.value.altText = '';
        this.value.touched = true;
        this.value.hasError = false;
        this.value.errorMessage = null;
        this.value.showAltText = false;
    }

    private uploadFile(file: File) {
        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            map(response => {
                if (response.mediaResults[0].codedError?.code === MediaServiceErrorCodesEnum.duplicateFile) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.contentLibrary.Browserupload.error',
                        detail: 'Common.contentLibrary.Browserupload.error.message'
                    });
                    return null;
                } else if (response.mediaResults[0].codedError?.code === MediaServiceErrorCodesEnum.fileMaxSize) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.mediaService.fileUpload.maxSize.heading',
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.maxSize.message', { name: file.name })
                    });
                    return null;
                }  else if (response.mediaResults[0].codedError) {
                    // fail safe if any image fails
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.contentLibrary.Browserupload.error',
                        detail: 'Common.lhp.libraryMoveDialog.upload.error.message'
                    });
                    return null;
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.contentLibrary.Browserupload',
                        detail: 'Common.contentLibrary.Browserupload.message'
                    });
                }
                return {
                    url: response.mediaResults[0].media.url,
                    name: response.mediaResults[0].media.name,
                    id: response.mediaResults[0].media.id,
                    size: response.mediaResults[0].media.size
                };
            }),
            filter(fileUpload => fileUpload !== null),
            tap(fileUpload => {
                this.assignMediaToValue(fileUpload);
                this.emitChanges();
                this.contentLibraryMediaService.refreshAll();
            })
        ).subscribe({
            error: (error) => {
                if (error.status === 429) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                    });
                }
            }
        });
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
